import Cookies from "js-cookie";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { UserLoginType } from "lib/types/user";
import { analyticsEvents, analyticsTypes, constants } from "lib/utils/constants";
import { getUTMParamsFromURL } from "lib/utils/helpers";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import useSendAnalyticsEvent from "./useAnalytics";
import { useUserContext } from "lib/contexts/UserProvider";
import { useToken } from "lib/contexts/TokenProvider";
import { useAuthContext } from "lib/contexts/AuthProvider";
import useAnonymousId from "./useAnonymousId";

export const useAnalyticsInitialization = () => {
  const {
    state: { checkoutId, shopifySessionId, checkoutItems, billing },
  } = useCheckoutContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { user },
    actions: { setUserLoginType },
  } = useUserContext();
  const {
    state: { tokenId: floTokenId },
  } = useToken();
  const {
    state: { loginType, userType },
  } = useAuthContext();
  const { initializeAnonymousId } = useAnonymousId();
  const { sendAnalyticsEvent, getLongSessionId } = useSendAnalyticsEvent();
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page")?.toLowerCase();

  // Handle checkout events when account response is available
  useEffect(() => {
    const shouldRunUserEvents =
      (!page || page?.toLowerCase() === "checkout") && checkoutId && merchant?.merchantId && user?.uid;

    if (shouldRunUserEvents) {
      handleCheckoutEvents();
    }
  }, [page, checkoutId, merchant?.merchantId, user?.uid]);

  // Handle UI loaded events
  useEffect(() => {
    const shouldRunUIEvents =
      (!page || page?.toLowerCase() === "checkout") && checkoutId && merchant?.merchantId;

    if (shouldRunUIEvents) {
      handleUILadedEvents();
    }
  }, [page, checkoutId, merchant?.merchantId]);

  const handleUILadedEvents = () => {
    const tokenId = floTokenId ?? searchParams.get("tokenId");
    // Send checkout UI loaded event
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_CHECKOUT_UI_LOADED,
      eventType: "load",
      metaData: {
        brandUrl: merchant?.brandUrl,
        merchantId: merchant?.merchantId,
        checkoutId: checkoutId,
        checkoutToken: tokenId,
        shopifySessionId: shopifySessionId,
        checkoutItems: checkoutItems,
      },
    });
  };

  const handleCheckoutEvents = () => {
    const tokenId = floTokenId ?? searchParams.get("tokenId");
    let loginTypeAnalytics = analyticsEvents.FLO_COOKIE_LOGIN;

    initializeAnonymousId(true);

    // Handle cookie-based login
    if (Cookies.get(constants.REFRESH_TOKEN_COOKIE_CLIENT) && loginType === "COOKIE") {
      setUserLoginType(analyticsEvents.FLO_COOKIE_LOGIN as UserLoginType);
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_COOKIE_LOGIN,
        eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
        eventType: "flo_action",
        metaData: {
          brandUrl: merchant?.brandUrl,
          merchantId: merchant?.merchantId,
          checkoutToken: tokenId,
          checkoutId: checkoutId,
          shopifySessionId: shopifySessionId,
          userType: analyticsEvents.FLO_COOKIE_LOGIN,
          userData: {
            type: analyticsEvents.FLO_COOKIE_LOGIN,
            uid: user?.uid,
            userId: user?.uid,
          },
          isFbqEnabled: merchant?.analytics?.facebook?.enabled ?? false,
          fbqPixelId: merchant?.analytics?.facebook?.pixel_id ?? [],
          checkoutItems: checkoutItems,
          totalPayable: billing?.total_payable,
        },
      });
    }
    // Handle guest login
    else if (
      userType === "NEW" ||
      Boolean(
        Cookies.get(constants.AUTH_COOKIE_CLIENT) && !Cookies.get(constants.REFRESH_TOKEN_COOKIE_CLIENT),
      )
    ) {
      loginTypeAnalytics = analyticsEvents.FLO_GUEST_LOGIN;
      setUserLoginType(analyticsEvents.FLO_GUEST_LOGIN as UserLoginType);
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_GUEST_LOGIN,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "flo_action",
        metaData: {
          brandUrl: merchant?.brandUrl,
          merchantId: merchant?.merchantId,
          checkoutToken: tokenId,
          checkoutId: checkoutId,
          shopifySessionId: shopifySessionId,
          userData: {
            type: analyticsEvents.FLO_GUEST_LOGIN,
          },
          checkoutItems: checkoutItems,
        },
      });
    }

    // Handle UTM parameters
    const utmParams = getUTMParamsFromURL();
    if (utmParams?.length) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_STORE_PAGE_VIEW,
        eventType: "load",
        metaData: {
          storeDetails: {
            utmParams: utmParams,
          },
          merchantId: merchant?.merchantId,
          checkoutId: checkoutId,
          checkoutToken: tokenId,
          shopifySessionId: shopifySessionId,
          userData: {
            type: analyticsEvents.FLO_GUEST_LOGIN,
          },
        },
      });
    }

    // Send auth login completed event
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_AUTH_LOGIN_COMPLETED,
      eventType: "load",
      metaData: {
        checkoutId: checkoutId,
        checkoutToken: tokenId,
        shopifySessionId: shopifySessionId,
        merchantId: merchant?.merchantId,
        userData: {
          type: loginTypeAnalytics,
          userId: user?.uid,
        },
        checkoutItems: checkoutItems,
        segmentId: checkoutId,
      },
    });
  };
};
